import { Link, useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import {
  SUBSCRIPTION_TIERS,
  type SubscriptionTier,
} from "@/lib/subscriptionTiers";

export default function CheckoutSubscribe() {
  const [params] = useSearchParams();
  const auditId = params.get("auditId");

  return (
    <MarketingLayout>
      <div className="mx-auto w-full max-w-5xl px-6 py-12">
        {auditId ? (
          <Link
            to={`/audit/${auditId}`}
            className="text-sm text-slate-500 hover:text-slate-900"
          >
            ← Back to audit
          </Link>
        ) : (
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">
            ← Home
          </Link>
        )}

        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
          Pick your monitoring plan
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Start with a free trial. After setup, choose the plan that matches
          your catalog size and alerting needs.
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {SUBSCRIPTION_TIERS.map((tier) => (
            <PlanCard key={tier.id} tier={tier} auditId={auditId} />
          ))}
        </div>
      </div>
    </MarketingLayout>
  );
}

function trialHref(tier: SubscriptionTier, auditId: string | null): string {
  const params = new URLSearchParams({ plan: tier.id });
  if (auditId) params.set("auditId", auditId);
  return `/start-trial?${params.toString()}`;
}

function PlanCard({
  tier,
  auditId,
}: {
  tier: SubscriptionTier;
  auditId: string | null;
}) {
  const popular = tier.popular;
  const cardCls = popular
    ? "relative rounded-2xl border-2 border-slate-900 bg-slate-900 p-7 text-white"
    : "relative rounded-2xl border border-slate-200 bg-white p-7";
  const blurbCls = popular
    ? "mt-2 text-sm text-slate-300"
    : "mt-2 text-sm text-slate-600";
  const quotaCls = popular
    ? "mt-4 text-sm font-medium text-emerald-300"
    : "mt-4 text-sm font-medium text-emerald-700";
  const checkCls = popular
    ? "mt-0.5 h-4 w-4 shrink-0 text-emerald-400"
    : "mt-0.5 h-4 w-4 shrink-0 text-emerald-600";
  const ctaCls = popular
    ? "bg-white text-slate-900 hover:bg-slate-100"
    : "bg-slate-900 text-white hover:bg-slate-800";

  return (
    <div className={cardCls}>
      {popular && (
        <span className="absolute -top-3 right-6 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white">
          Most popular
        </span>
      )}
      <h2 className="text-lg font-semibold">{tier.name}</h2>
      <p className={blurbCls}>{tier.blurb}</p>
      <div className="mt-5 text-3xl font-semibold tracking-tight">
        ${tier.priceMonthly}
        <span className="text-base font-normal opacity-70">/mo</span>
      </div>
      <p className={quotaCls}>{tier.quota}</p>
      <ul className="mt-6 space-y-2.5 text-sm">
        {tier.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Check className={checkCls} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <Link
        to={trialHref(tier, auditId)}
        className={`mt-7 inline-flex w-full items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold transition ${ctaCls}`}
      >
        Start free trial →
      </Link>
    </div>
  );
}
